// Builds a COMPLETE text dump of the whole dashboard to send to the assistant.
// Granularity goes all the way down — per product / per SKU / per month / per
// batch — so the model can answer detailed questions (e.g. average monthly Sell
// In units of a specific SKU in a country). Opus 4.8 has a 1M-token context and
// volume is low, so completeness beats compactness here.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (v) => {
  const n = Number(v) || 0, s = n < 0 ? '-' : '', a = Math.abs(n);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${Math.round(a)}`;
};
const num = (v) => Math.round(Number(v) || 0).toLocaleString();
const sum = (a) => (Array.isArray(a) ? a : Object.values(a || {})).reduce((x, y) => x + (Number(y) || 0), 0);
// Compact comma-joined monthly array (rounded).
const arr = (a) => (Array.isArray(a) ? a.map((v) => Math.round(Number(v) || 0)).join(',') : '');
const hasVals = (a) => Array.isArray(a) && a.some((v) => Number(v));

// ---------------- Sales & Forecast ----------------
function forecastSection(forecastData) {
  if (!forecastData || typeof forecastData !== 'object') return '';
  const rows = [];
  const prodBlocks = [];
  let tSales = 0, tAop = 0, tCogs = 0, tSmm = 0, tGp = 0, lastFirm = -1;
  Object.entries(forecastData).forEach(([key, c]) => {
    if (!c || typeof c !== 'object') return;
    const sales = sum(c.actualSales);
    const aop = sum(c.aopMonthly) || Number(c.aop) || 0;
    const cogs = sum(c.cogsMonthly), smm = sum(c.smmMonthly);
    const gp = sales - cogs - smm;
    tSales += sales; tAop += aop; tCogs += cogs; tSmm += smm; tGp += gp;
    if (Array.isArray(c.monthStatus)) c.monthStatus.forEach((s, i) => { if (s === 'actual' && i > lastFirm) lastFirm = i; });
    const pct = aop > 0 ? Math.round((sales / aop) * 100) : null;
    rows.push(`  - ${c.country || key}: Sales(ACT+FC) ${money(sales)}, AOP ${money(aop)}, ${pct != null ? pct + '% of plan' : 'no plan'}, COGS ${money(cogs)}, SMM ${money(smm)}, GP ${money(gp)}`);
    // Monthly detail (USD) + per-product units.
    const lines = [];
    if (hasVals(c.actualSales)) lines.push(`      monthly sales USD: ${arr(c.actualSales)}`);
    if (hasVals(c.aopMonthly)) lines.push(`      monthly AOP USD: ${arr(c.aopMonthly)}`);
    if (hasVals(c.units)) lines.push(`      monthly units: ${arr(c.units)}`);
    if (Array.isArray(c.monthStatus) && c.monthStatus.length) lines.push(`      month status: ${c.monthStatus.join(',')}`);
    if (Array.isArray(c.products)) c.products.forEach((p) => {
      if (hasVals(p.units)) lines.push(`      product "${p.name}"${p.code ? ` (${p.code})` : ''} units: ${arr(p.units)} (total ${num(sum(p.units))})`);
    });
    if (lines.length) prodBlocks.push(`### ${c.country || key} — monthly detail (Jan..Dec)\n${lines.join('\n')}`);
  });
  if (!rows.length) return '';
  const ach = tAop > 0 ? ((tSales / tAop) * 100).toFixed(1) : '—';
  const firm = lastFirm >= 0 ? `firm (actual) through ${MONTHS[lastFirm]}, forecast after` : 'firm/forecast split not marked';
  return `## Sales & Forecast (FY, USD; months Jan..Dec = index 0..11)
Definitions: GP (Gross Profit) = Sales − COGS − SMM. COGS = Cost of Goods Sold. SMM = Sales & Marketing Margin.
LATAM total: Sales(ACT+FC) ${money(tSales)}, AOP ${money(tAop)}, achievement ${ach}%, COGS ${money(tCogs)}, SMM ${money(tSmm)}, Gross Profit ${money(tGp)} (${tSales > 0 ? Math.round((tGp / tSales) * 100) : 0}% margin). Months ${firm}.
By country (annual):
${rows.join('\n')}

${prodBlocks.join('\n')}`.trim();
}

// Mexico business rule (mirror of SellInModule.mergeMutualIntoPublic): fold the
// "Mutual" channel into "Public" so the assistant matches what the dashboard shows.
const addArr12 = (a, b) => Array.from({ length: 12 }, (_, i) => (a?.[i] || 0) + (b?.[i] || 0));
const cloneMetric = (m) => Object.fromEntries(Object.entries(m || {}).map(([y, a]) => [y, Array.isArray(a) ? [...a] : []]));
function mergeMutualIntoPublic(country) {
  if (!country?.channels) return country;
  const pubIdx = country.channels.findIndex((c) => /públic|public/i.test(c.name));
  const mutIdx = country.channels.findIndex((c) => /mutual/i.test(c.name));
  if (pubIdx < 0 || mutIdx < 0) return country;
  const map = new Map((country.channels[pubIdx].products || []).map((p) => [p.name, { name: p.name, units: cloneMetric(p.units), values: cloneMetric(p.values) }]));
  (country.channels[mutIdx].products || []).forEach((sp) => {
    const tp = map.get(sp.name);
    if (tp) {
      Object.entries(sp.units || {}).forEach(([y, a]) => { tp.units[y] = addArr12(tp.units[y], a); });
      Object.entries(sp.values || {}).forEach(([y, a]) => { tp.values[y] = addArr12(tp.values[y], a); });
    } else {
      map.set(sp.name, { name: sp.name, units: cloneMetric(sp.units), values: cloneMetric(sp.values) });
    }
  });
  const mergedPublic = { ...country.channels[pubIdx], name: 'Public', products: [...map.values()] };
  const channels = country.channels.map((c, i) => (i === pubIdx ? mergedPublic : c)).filter((_, i) => i !== mutIdx);
  return { ...country, channels };
}

// ---------------- Sell In (full: country → channel → product → month) ----------------
function sellInSection(sellInData) {
  const countries = sellInData?.countries;
  if (!countries || typeof countries !== 'object') return '';
  const out = ['## Sell In — units (pieces) and values (local currency) sold to distributors',
    'Full detail: country → channel → product (SKU) → 12 monthly numbers per year (Jan..Dec = index 0..11).',
    'IMPORTANT for averages: "reported months" = how many months that country-year actually has data (for the current/YTD year this is < 12). The per-line "avg X/mo" ALREADY divides the total by reported months — use it as THE monthly average. Never divide a YTD annual total by 12; divide by reported months.'];
  Object.entries(countries).forEach(([key, c0]) => {
    // Mexico: Mutual is folded into Public (same rule the dashboard applies).
    const c = key === 'mexico' ? mergeMutualIntoPublic(c0) : c0;
    if (!c?.channels) return;
    const years = [...(c.years || [])].sort((a, b) => a - b);
    // Reported months per year = (last month index, across ALL products/channels, that has any data) + 1.
    // A month reported as 0 still counts once any product in the country has data that far.
    const reported = {};
    years.forEach((y) => {
      let last = -1;
      c.channels.forEach((ch) => (ch.products || []).forEach((p) => {
        const u = p.units?.[y] || [];
        for (let i = 0; i < u.length; i++) if (Number(u[i])) last = Math.max(last, i);
      }));
      reported[y] = last + 1;
    });
    out.push(`\n### ${c.name} [${c.currency || ''}]${c.hasValues === false ? ' (UNITS ONLY, no values)' : ''} — years: ${years.map((y) => `${y} (${reported[y]} reported months)`).join(', ')}`);
    c.channels.forEach((ch) => {
      out.push(`Channel "${ch.name}":`);
      (ch.products || []).forEach((p) => {
        out.push(`  • ${p.name}`);
        years.forEach((y) => {
          const rm = reported[y] || 12;
          const u = p.units?.[y];
          if (hasVals(u)) out.push(`      units ${y}: ${arr(u)}  (total ${num(sum(u))} over ${rm} reported months → avg ${num(sum(u) / rm)}/mo)`);
          const v = p.values?.[y];
          if (hasVals(v)) out.push(`      values ${y}: ${arr(v)}  (total ${num(sum(v))} over ${rm} reported months → avg ${num(sum(v) / rm)}/mo)`);
        });
      });
    });
  });
  return out.length > 3 ? out.join('\n') : '';
}

// ---------------- Sell Out (full: country → SKU → month) ----------------
function sellOutSection(sellOutData) {
  if (!sellOutData || typeof sellOutData !== 'object') return '';
  const out = ['## Sell Out — in-market demand (IQVIA), by country → SKU → month',
    'Each SKU has monthly values (local currency) and units, aligned 1:1 to the months list. Rows marked (GROUP) are the sum of their member SKUs.'];
  let any = false;
  Object.values(sellOutData).forEach((c) => {
    if (!c?.skus || !c?.months) return;
    any = true;
    out.push(`\n### ${c.name} [${c.currency || ''}] — months: ${c.months.join(', ')}`);
    c.skus.forEach((s) => {
      const label = s.isGroup ? `${s.name} (GROUP total)` : `${s.name}${s.group ? ` (group ${s.group})` : ''}`;
      out.push(`  • ${label}`);
      if (hasVals(s.units)) out.push(`      units: ${arr(s.units)} (total ${num(sum(s.units))})`);
      if (hasVals(s.values)) out.push(`      values: ${arr(s.values)} (total ${num(sum(s.values))})`);
    });
  });
  return any ? out.join('\n') : '';
}

// ---------------- Inventory (full: country → SKU → batch) ----------------
function inventorySection(inventoryData) {
  if (!inventoryData || typeof inventoryData !== 'object' || Array.isArray(inventoryData)) return '';
  const now = Date.now();
  const rslMo = (exp) => { const d = (new Date(exp) - now) / 86400000; return isNaN(d) ? null : d / 30.44; };
  const out = ['## Inventory & RSL (NOTE: may not be fully up to date) — country → SKU → batch',
    'RSL = remaining shelf life (months to expiry). Each batch: units, USD value, expiry date, RSL.'];
  let any = false;
  Object.values(inventoryData).forEach((m) => {
    if (!m?.skus) return;
    any = true;
    out.push(`\n### ${m.name}`);
    m.skus.forEach((sku) => {
      const months = (sku.batches || []).map((b) => rslMo(b.expDate)).filter((x) => x != null);
      const minRsl = months.length ? Math.min(...months) : null;
      out.push(`  • ${sku.name}: ${num(sku.totalUnits)}u, ${money(sku.totalUsd)}, ${(sku.batches || []).length} batch(es)${minRsl != null ? `, min RSL ${minRsl.toFixed(1)}mo` : ''}`);
      (sku.batches || []).forEach((b) => {
        const r = rslMo(b.expDate);
        out.push(`      batch ${b.batch || '—'}: ${num(b.units)}u, ${money(b.usd)}, exp ${b.expDate || '—'}${r != null ? `, RSL ${r.toFixed(1)}mo` : ''}`);
      });
    });
  });
  return any ? out.join('\n') : '';
}

// ---------------- Digital ----------------
function digitalSection(d) {
  if (!d || typeof d !== 'object') return '';
  const parts = [];
  if (d.luyeLearning?.length) {
    const series = d.luyeLearning.map((r) => `${r.period}=${num(r.visitors)}`).join(', ');
    parts.push(`  - Luye Learning (educational web platform, visitors per quarter) — all periods:\n      ${series}`);
  }
  if (d.mentalHealth?.length) {
    const series = d.mentalHealth.map((r) => `${r.year}: Public ${num(r.public)} + Private ${num(r.private)} = ${num((r.public || 0) + (r.private || 0))}`).join('; ');
    parts.push(`  - Mental Health (annual event, Mexico physicians, by sector) — all editions:\n      ${series}`);
  }
  if (d.symposium?.editions?.length) {
    const countries = d.symposium.countries || [];
    const eds = d.symposium.editions.filter((e) => (e.total || 0) > 0 || Object.values(e.values || {}).some((v) => v));
    const lines = eds.map((e) => `${e.year}: total ${num(e.total)} (${countries.map((c) => `${c} ${num(e.values?.[c] || 0)}`).join(', ')})`);
    parts.push(`  - LATAM Symposium (annual event, physicians enrolled by country) — all editions:\n      ${lines.join('\n      ')}`);
  }
  if (!parts.length) return '';
  return `## Digital Strategy\n${parts.join('\n')}`;
}

// ---------------- Insights / Projects / Deals / Business Case ----------------
function insightsSection(insightsData) {
  const m = insightsData?.countries;
  if (!m || typeof m !== 'object') return '';
  const rows = Object.entries(m).filter(([, v]) => v?.text || v?.label).map(([k, v]) => `  - ${k}: [${v.label || '—'}] ${v.text || ''}`.trim());
  if (!rows.length) return '';
  return `## Manual Insights / notes (team's editorial commentary — NARRATIVE ONLY, may be outdated; for any number use the data sections above, not these notes)\n${rows.join('\n')}`;
}

function projectsSection(projects) {
  if (!Array.isArray(projects) || !projects.length) return '';
  const today = Date.now();
  const rows = projects.map((p) => {
    const subs = Array.isArray(p.subtasks) ? p.subtasks : [];
    const done = subs.filter((s) => s.status === 'completed').length;
    const open = subs.length - done;
    const overdue = subs.filter((s) => s.status !== 'completed' && s.dueDate && new Date(s.dueDate).getTime() < today).length;
    const head = `  - ${p.name}${p.country ? ` (${p.country})` : ''}${p.partner ? `, partner ${p.partner}` : ''}: ${subs.length} tasks — ${done} done, ${open} open${overdue ? `, ${overdue} overdue` : ''}`;
    const taskLines = subs.map((s) => `      · ${s.title || 'task'} [${s.status || 'open'}]${s.assignedTo ? `, assigned ${s.assignedTo}` : ''}${s.dueDate ? `, due ${String(s.dueDate).slice(0, 10)}` : ''}`);
    return taskLines.length ? `${head}\n${taskLines.join('\n')}` : head;
  });
  return `## LATAM Projects (initiatives & team tasks)\n${rows.join('\n')}`;
}

const DEAL_STAGES = ['Market Evaluation', 'Partner Evaluation', 'Scouting', 'CDA', 'BC', 'TS', 'Agreements', 'DD', 'MA Submission', 'MA Approval', 'Prelaunch Activities', 'Product Launch'];
const DEAL_STATUS = { green: 'completed', yellow: 'in progress', red: 'blocked', none: 'pending' };
function dealsSection(deals) {
  const rows = deals?.projects;
  if (!Array.isArray(rows) || !rows.length) return '';
  const tabLabel = { patches: 'Patches', lai: 'LAI', biosimilars: 'Biosimilars' };
  const byTab = {};
  rows.forEach((r) => {
    const stages = r.stages || {};
    const texts = r.stage_texts || {};
    const done = Object.values(stages).filter((c) => c === 'green').length;
    let current = '';
    for (let i = DEAL_STAGES.length - 1; i >= 0; i--) { const v = stages[DEAL_STAGES[i]]; if (v && v !== 'none') { current = DEAL_STAGES[i]; break; } }
    // Every stage with status and/or a typed milestone date.
    const stageDetail = DEAL_STAGES.filter((s) => (stages[s] && stages[s] !== 'none') || texts[s])
      .map((s) => `${s}${texts[s] ? ` ${texts[s]}` : ''} [${DEAL_STATUS[stages[s]] || 'pending'}]`);
    let line = `  - ${r.partner || '—'}${r.product ? ` / ${r.product}` : ''}${r.country ? ` (${r.country})` : ''}: ${done}/12 completed${current ? `, latest "${current}"` : ''}${r.expected ? `, expected ${r.expected}` : ''}${r.downpayments ? `, downpayment ${r.downpayments}` : ''}`;
    if (stageDetail.length) line += `\n      stages — ${stageDetail.join('; ')}`;
    (byTab[r.tab || 'other'] = byTab[r.tab || 'other'] || []).push(line);
  });
  const blocks = Object.entries(byTab).map(([tab, lines]) => `${tabLabel[tab] || tab}:\n${lines.join('\n')}`);
  return `## Deal Tracker (partner pipeline by product line; 12 stages Market Evaluation → Product Launch; status completed/in progress/blocked/pending; milestone text is the date typed per stage)\n${blocks.join('\n')}`;
}

function businessCaseSection(cases) {
  if (!Array.isArray(cases) || !cases.length) return '';
  const rows = cases.map((c) => `  - ${c.name || 'Untitled'}${c.country ? ` (${c.country})` : ''}${c.product ? `, ${c.product}` : ''}${c.partner ? `, partner ${c.partner}` : ''}`);
  return `## Business Case (saved cases — open the Business Case module for the full P&L of each)\n${rows.join('\n')}`;
}

export function buildAssistantContext(data = {}) {
  const { forecastData, sellInData, sellOutData, inventoryData, digitalData, insightsData, projects, deals, businessCases } = data;
  // Each section is isolated: a throw in ONE (from a surprising data shape) must
  // not drop the others, and must never white-screen the app (this runs in a
  // useMemo with no error boundary above it).
  const safe = (fn, arg) => { try { return fn(arg) || ''; } catch (e) { console.error(`assistant ${fn.name} failed:`, e); return ''; } };
  const sections = [
    safe(forecastSection, forecastData),
    safe(sellInSection, sellInData),
    safe(sellOutSection, sellOutData),
    safe(inventorySection, inventoryData),
    safe(digitalSection, digitalData),
    safe(dealsSection, deals),
    safe(projectsSection, projects),
    safe(businessCaseSection, businessCases),
    safe(insightsSection, insightsData),
  ].filter(Boolean);
  if (!sections.length) return 'No dashboard data has been loaded yet.';
  return sections.join('\n\n');
}
